// ============================================================================
// VOWBOT FRONTEND FORMATTERS
// ============================================================================
// Pure functions for formatting VowBot messages and UI elements
// Client-side display logic and formatting helpers
// ============================================================================

// ============================================================================
// MESSAGE CARD FORMATTERS
// ============================================================================

/**
 * Format vendor card data for display
 * @param {Object} vendor - Vendor object with theme match data
 * @returns {Object} Formatted vendor card data
 */
export const formatVendorCard = (vendor) => {
  return {
    id: vendor.id || vendor.vendor_id,
    name: vendor.name || vendor.vendor_name,
    category: vendor.category,
    location: vendor.location,
    rating: vendor.rating || 0,
    priceRange: vendor.price_range || 'Contact for pricing',  // Display symbols directly: $$, $$$, etc.
    themeMatch: vendor.themeMatch || null,
    matchBadge: vendor.matchBadge || null,
    matchReasons: vendor.matchReasons || [],
    hasThemeMatch: !!vendor.themeMatch
  };
};

/**
 * Format progress card data for display
 * @param {Object} context - User context object
 * @returns {Object} Formatted progress data
 */
export const formatProgressCard = (context) => {
  if (!context) return null;
  
  const progress = calculateProgressPercentage(context);
  const gaps = identifyDisplayGaps(context);
  
  return {
    overall: progress,
    status: getProgressStatus(progress),
    breakdown: {
      theme: context.metrics?.hasTheme ? 100 : 0,
      date: context.metrics?.hasDate ? 100 : 0,
      budget: context.metrics?.hasBudget ? 100 : 0,
      vendors: Math.min((context.metrics?.shortlistCount || 0) / 7 * 100, 100)
    },
    counts: {
      themes: context.metrics?.themeCount || 0,
      vendors: context.metrics?.shortlistCount || 0,
      daysUntil: context.wedding?.daysUntil || null
    },
    gaps: gaps,
    urgency: context.wedding?.urgency || 'unknown'
  };
};

/**
 * Format timeline card data for display
 * @param {Array} tasks - Timeline tasks
 * @param {number} daysUntil - Days until wedding
 * @returns {Object} Formatted timeline data
 */
export const formatTimelineCard = (tasks, daysUntil = null) => {
  if (!tasks || tasks.length === 0) return null;
  
  return {
    tasks: tasks.slice(0, 5).map(task => ({
      title: task.task || task.title,
      description: task.description,
      monthsBefore: task.months_before,
      suggestedDate: task.suggested_date,
      urgency: getTaskUrgency(task.months_before, daysUntil),
      icon: getTaskIcon(task.category)
    })),
    totalTasks: tasks.length,
    daysUntil: daysUntil
  };
};

// ============================================================================
// QUICK ACTION GENERATORS
// ============================================================================

/**
 * Generate smart quick actions based on user context
 * @param {Object} context - User context object
 * @returns {Array} Array of quick action objects
 */
export const generateSmartQuickActions = (context) => {
  const actions = [];
  
  if (!context) {
    // Default actions for users without context
    return [
      { text: "Plan my budget", icon: "💰", gradient: "from-emerald-400 to-teal-500" },
      { text: "Find vendors", icon: "🏪", gradient: "from-purple-400 to-pink-500" },
      { text: "Create timeline", icon: "📅", gradient: "from-blue-400 to-indigo-500" },
      { text: "Design theme", icon: "🎨", gradient: "from-rose-400 to-pink-500" }
    ];
  }
  
  // Smart actions based on user progress
  if (!context.metrics?.hasTheme) {
    actions.push({ 
      text: "Create my theme", 
      icon: "🎨", 
      gradient: "from-rose-400 to-pink-500",
      priority: 1
    });
  }
  
  if (context.metrics?.hasTheme && context.metrics?.shortlistCount < 3) {
    actions.push({ 
      text: "Find vendors", 
      icon: "🏪", 
      gradient: "from-purple-400 to-pink-500",
      priority: 2
    });
  }
  
  if (!context.metrics?.hasBudget) {
    actions.push({ 
      text: "Plan my budget", 
      icon: "💰", 
      gradient: "from-emerald-400 to-teal-500",
      priority: 3
    });
  }
  
  if (context.wedding?.daysUntil && context.wedding.daysUntil < 180 && !context.metrics?.hasTimeline) {
    actions.push({ 
      text: "Create timeline", 
      icon: "📅", 
      gradient: "from-blue-400 to-indigo-500",
      priority: 1
    });
  }
  
  // If no specific actions, show general ones
  if (actions.length === 0) {
    actions.push(
      { text: "View my progress", icon: "📊", gradient: "from-indigo-400 to-purple-500" },
      { text: "Explore vendors", icon: "🔍", gradient: "from-cyan-400 to-blue-500" }
    );
  }
  
  // Sort by priority and return top 4
  return actions.sort((a, b) => (a.priority || 99) - (b.priority || 99)).slice(0, 4);
};

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Get color for match badge based on score
 * @param {number} score - Match score (0-100)
 * @returns {string} Tailwind color class
 */
export const getMatchBadgeColor = (score) => {
  if (score >= 90) return 'bg-green-500';
  if (score >= 80) return 'bg-blue-500';
  if (score >= 70) return 'bg-yellow-500';
  if (score >= 60) return 'bg-orange-500';
  return 'bg-gray-500';
};

/**
 * Format budget amount for display
 * @param {number} amount - Budget amount
 * @returns {string} Formatted budget string
 */
export const formatBudgetDisplay = (amount) => {
  if (!amount) return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Get urgency icon based on days until wedding
 * @param {number} daysUntil - Days until wedding
 * @returns {string} Urgency icon emoji
 */
export const getUrgencyIcon = (daysUntil) => {
  if (!daysUntil || daysUntil < 0) return '📅';
  if (daysUntil <= 30) return '🚨';
  if (daysUntil <= 90) return '⚠️';
  if (daysUntil <= 180) return '📌';
  return '📅';
};

/**
 * Get progress status label
 * @param {number} progress - Progress percentage
 * @returns {Object} Status with label and color
 */
export const getProgressStatus = (progress) => {
  if (progress >= 80) return { label: 'Almost There!', color: 'text-green-600' };
  if (progress >= 50) return { label: 'Great Progress', color: 'text-blue-600' };
  if (progress >= 25) return { label: 'Getting Started', color: 'text-yellow-600' };
  return { label: 'Just Beginning', color: 'text-gray-600' };
};

/**
 * Format relative date display
 * @param {number} daysUntil - Days until wedding
 * @returns {string} Formatted date string
 */
export const formatRelativeDate = (daysUntil) => {
  if (!daysUntil || daysUntil < 0) return 'Past';
  if (daysUntil === 0) return 'Today!';
  if (daysUntil === 1) return 'Tomorrow!';
  if (daysUntil <= 7) return `${daysUntil} days`;
  if (daysUntil <= 30) return `${Math.ceil(daysUntil / 7)} weeks`;
  if (daysUntil <= 365) return `${Math.ceil(daysUntil / 30)} months`;
  return `${Math.ceil(daysUntil / 365)} years`;
};

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Calculate overall progress percentage
 * @param {Object} context - User context
 * @returns {number} Progress percentage
 */
const calculateProgressPercentage = (context) => {
  let score = 0;
  
  if (context.metrics?.hasTheme) score += 20;
  if (context.metrics?.hasDate) score += 10;
  if (context.metrics?.hasBudget) score += 10;
  
  const vendorCount = context.metrics?.shortlistCount || 0;
  score += Math.min(vendorCount * 10, 60);
  
  return Math.min(score, 100);
};

/**
 * Identify gaps for display
 * @param {Object} context - User context
 * @returns {Array} Array of gap objects
 */
const identifyDisplayGaps = (context) => {
  const gaps = [];
  
  if (!context.metrics?.hasTheme) {
    gaps.push({ type: 'theme', label: 'Create Theme', icon: '🎨' });
  }
  
  if (!context.metrics?.hasDate) {
    gaps.push({ type: 'date', label: 'Set Date', icon: '📅' });
  }
  
  if (!context.metrics?.hasBudget) {
    gaps.push({ type: 'budget', label: 'Set Budget', icon: '💰' });
  }
  
  const vendorCount = context.metrics?.shortlistCount || 0;
  if (vendorCount < 3) {
    gaps.push({ type: 'vendors', label: 'Find Vendors', icon: '🏪' });
  }
  
  return gaps;
};

/**
 * Get task urgency level
 * @param {number} monthsBefore - Months before wedding
 * @param {number} daysUntil - Days until wedding
 * @returns {string} Urgency level
 */
const getTaskUrgency = (monthsBefore, daysUntil) => {
  if (!daysUntil) return 'normal';
  
  const taskDays = monthsBefore * 30;
  if (daysUntil <= taskDays) return 'urgent';
  if (daysUntil <= taskDays + 30) return 'soon';
  return 'normal';
};

/**
 * Get icon for task category
 * @param {string} category - Task category
 * @returns {string} Icon emoji
 */
const getTaskIcon = (category) => {
  const icons = {
    venue: '🏛️',
    vendors: '🏪',
    invitations: '💌',
    attire: '👗',
    decorations: '🎨',
    catering: '🍽️',
    photography: '📸',
    music: '🎵'
  };
  return icons[category?.toLowerCase()] || '📋';
};
